import * as React from 'react';

import {useMutation} from '@apollo/react-hooks';

import {colors} from 'shells/colors/colors';
import type {EmailMessage} from 'shells/email-row-item/types';
// @ts-expect-error - imported from .js/.jsx file
import {CloseIcon} from 'shells/icon/svg/close-icon';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
import {ModalLightbox} from 'shells/modal/modal-lightbox';
import {ModalToolbar} from 'shells/modal/modal-toolbar';
import {EmailPreviewHeader} from 'shells/timeline/ribbon/email-preview-header';

import type {
    DeleteEmail as DeleteEmailMutation,
    DeleteEmailVariables as DeleteEmailMutationVariables,
    ShareEmail as ShareEmailMutation,
    ShareEmailVariables as ShareEmailMutationVariables,
} from 'nutshell-graphql-types';

import {GetEmailDetails} from '../mailbox/get-email-details';
import DELETE_EMAIL from '../mailbox/graphql/mutations/delete-email.graphql';
import SHARE_EMAIL from '../mailbox/graphql/mutations/share-email.graphql';

import styles from './email-preview-modal.module.css';

type Props = {
    onClose: () => any;
    emailId: string;
    onSaveCallback?: () => void;
    onDeleteCallback?: () => void;
    emailReplyTo?: (ReplyType: 'all' | 'single') => void;
};

export function EmailPreviewModal(props: Props) {
    return (
        <GetEmailDetails emailId={props.emailId} isQueued={Boolean(false)}>
            {({emailMessage, isLoading}) => {
                return (
                    <ModalLightbox onClose={props.onClose}>
                        <div className={styles['modal-container']}>
                            <button onClick={props.onClose} data-tip='Close'>
                                <CloseIcon size={17} />
                            </button>
                            <div className={styles['content-container']}>
                                <ModalContent
                                    email={emailMessage}
                                    isLoading={isLoading}
                                    onClose={props.onClose}
                                    emailReplyTo={props.emailReplyTo}
                                    onSaveCallback={props.onSaveCallback}
                                    onDeleteCallback={props.onDeleteCallback}
                                />
                            </div>
                        </div>
                    </ModalLightbox>
                );
            }}
        </GetEmailDetails>
    );
}

type ModalContentProps = {
    email: EmailMessage | null | undefined;
    isLoading: boolean;
    onClose: () => any;
    onSaveCallback?: () => void;
    onDeleteCallback?: () => void;
    emailReplyTo?: (ReplyType: 'all' | 'single') => void;
};

function ModalContent(props: ModalContentProps) {
    const [deleteEmail] = useMutation<DeleteEmailMutation, DeleteEmailMutationVariables>(
        DELETE_EMAIL
    );

    const [shareEmail] = useMutation<ShareEmailMutation, ShareEmailMutationVariables>(SHARE_EMAIL);

    const {email} = props;
    const emailReplyTo = props.emailReplyTo;

    return (
        <>
            {props.isLoading ? (
                <div className={styles.loading}>
                    <LoadingIcon fill={colors.greyLt} />
                </div>
            ) : undefined}
            {email ? (
                <>
                    <EmailPreviewHeader
                        actor={email.from}
                        verb={email.subject ? 'emailed' : 'responded to a ticket'}
                        participants={email.to}
                        subject={email.subject}
                        sentTime={email.sentTime ? email.sentTime : undefined}
                    />
                    <div
                        dangerouslySetInnerHTML={{
                            __html: email.bodyHtml,
                        }}
                        className={styles['email-content']}
                    />
                </>
            ) : undefined}
            {emailReplyTo ? (
                <ModalToolbar
                    onConfirmDelete={() => {
                        if (email) {
                            deleteEmail({
                                variables: {
                                    payload: {
                                        email: {
                                            id: email.id,
                                        },
                                    },
                                },
                            }).then(() => {
                                props.onClose(); // Closing the modal after the email has been deleted
                                if (props.onDeleteCallback) {
                                    props.onDeleteCallback();
                                }
                            });
                        }
                    }}
                    onClickHide={() => {
                        if (email) {
                            shareEmail({
                                variables: {
                                    payload: {
                                        emailId: email.id,
                                        isShared: !email.isShared,
                                    },
                                },
                            }).then(() => {
                                if (props.onSaveCallback) {
                                    props.onSaveCallback();
                                }
                            });
                        }
                    }}
                    isShared={Boolean(email && email.isShared)}
                    isShareable={Boolean(email && email.isShareable)}
                    // @ts-expect-error
                    onClickDownload={() => {}}
                    replyOptions={{
                        onClickReplyAll: () => {
                            emailReplyTo('all');
                        },
                        onClickReply: () => {
                            emailReplyTo('single');
                        },
                    }}
                />
            ) : undefined}
        </>
    );
}
